import { useState, type ReactNode } from 'react';

/**
 * Lightweight Bootstrap-styled hover tooltip. Replaces the native browser
 * `title=` tooltip with a dark popover that appears above the trigger on
 * hover/focus. No JS dependency on Bootstrap.js.
 */
export function HoverTooltip({
  text,
  children,
  placement = 'top',
  maxWidth = 220,
}: {
  text: string;
  children: ReactNode;
  placement?: 'top' | 'bottom' | 'left' | 'right';
  maxWidth?: number;
}) {
  const [open, setOpen] = useState(false);

  const isHorizontal = placement === 'left' || placement === 'right';

  const tipStyle: React.CSSProperties = isHorizontal
    ? {
        position: 'absolute',
        top: '50%',
        transform: 'translateY(-50%)',
        [placement === 'left' ? 'right' : 'left']: 'calc(100% + 8px)',
        backgroundColor: 'rgba(33, 37, 41, 0.95)',
        color: '#fff',
        padding: '8px 12px',
        borderRadius: 4,
        fontSize: 13,
        lineHeight: 1.4,
        textAlign: 'center',
        maxWidth,
        width: 'max-content',
        zIndex: 1080,
        pointerEvents: 'none',
        boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
      }
    : {
        position: 'absolute',
        left: '50%',
        transform: 'translateX(-50%)',
        [placement === 'top' ? 'bottom' : 'top']: 'calc(100% + 8px)',
        backgroundColor: 'rgba(33, 37, 41, 0.95)',
        color: '#fff',
        padding: '8px 12px',
        borderRadius: 4,
        fontSize: 13,
        lineHeight: 1.4,
        textAlign: 'center',
        maxWidth,
        width: 'max-content',
        zIndex: 1080,
        pointerEvents: 'none',
        boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
      };

  const arrowStyle: React.CSSProperties = isHorizontal
    ? {
        position: 'absolute',
        top: '50%',
        transform: 'translateY(-50%)',
        [placement === 'left' ? 'right' : 'left']: -5,
        width: 0,
        height: 0,
        borderTop: '6px solid transparent',
        borderBottom: '6px solid transparent',
        [placement === 'left' ? 'borderLeft' : 'borderRight']:
          '6px solid rgba(33, 37, 41, 0.95)',
      }
    : {
        position: 'absolute',
        left: '50%',
        transform: 'translateX(-50%)',
        [placement === 'top' ? 'bottom' : 'top']: -5,
        width: 0,
        height: 0,
        borderLeft: '6px solid transparent',
        borderRight: '6px solid transparent',
        [placement === 'top' ? 'borderTop' : 'borderBottom']:
          '6px solid rgba(33, 37, 41, 0.95)',
      };

  return (
    <span
      style={{ position: 'relative', display: 'inline-block' }}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
    >
      {children}
      {open && (
        <span style={tipStyle} role="tooltip">
          {text}
          <span style={arrowStyle} />
        </span>
      )}
    </span>
  );
}
